import type { IPhoto } from '@bridebook/models/source/models/Suppliers/Photos.types';
import type { Slug } from '@bridebook/toolbox/src/types';
import { ISupplierPhoto } from '@bridebook/web/app-shared/lib/supplier/supplier-types';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';

/**
 * Get correct photo object to be used as thumbnail.
 * In case of preferred suppliers list it is Object,
 * in most other cases it is array, so take first object in array.
 * @method getSupplierPhotoObject
 * @param {Object | Array} photos
 */
export const getSupplierPhotoObject = (
  photos?: IPhoto | IPhoto[] | ISupplierPhoto | ISupplierPhoto[] | string,
): IPhoto | ISupplierPhoto | null => {
  if (photos && typeof photos === 'object' && !Array.isArray(photos)) {
    return photos;
  }
  if (Array.isArray(photos) && photos.length > 0) {
    return photos[0];
  }

  return null;
};

const hasExtension = (path?: string) => !!path && /\.(gif|jpe?g|tiff|png|bmp|webp)$/i.test(path);

const getImgPath = <T extends { thumbnail?: string; thumbext?: string }>(supplier: T) =>
  (hasExtension(supplier?.thumbnail)
    ? supplier.thumbnail!
    : `${supplier.thumbnail}.${supplier?.thumbext || 'jpg'}`
  ).replace(/^suppliers\//, 'weddingsuppliers/');

const isVenue = (supplier: GetSupplierThumbUrlArg) =>
  supplier?.slug === 'venue' || supplier?.type === 'venue' || supplier?.type?.[0] === 'venue';

type GetImagePathArg = Parameters<typeof getImgPath>[0];

const getSupplierThumbUrl = <
  T extends {
    type?: Slug;
    slug?: Slug;
  } & GetImagePathArg,
>(
  supplier?: T,
): string =>
  `${IMAGES_BASE_URL}/${
    supplier?.thumbnail
      ? getImgPath(supplier)
      : `assets/supplier/${isVenue(supplier) ? 'img-custom-venue' : 'img-custom-supplier'}.jpg`
  }`;

type GetSupplierThumbUrlArg = Parameters<typeof getSupplierThumbUrl>[0];

export interface IGetSupplierThumb
  extends Omit<NonNullable<GetSupplierThumbUrlArg>, 'type' | 'slug'> {
  photos?: string;
  customSupplier?: boolean;
}

/**
 * Get supplier thumbnail photo.
 * If no thumbnail, get first photo.
 * If no photo, get supplier stock photo.
 * @method getSupplierThumb
 * @param {Object} supplierData
 * @param {IPhoto[]} photos
 */
export const getSupplierThumb = <T extends IGetSupplierThumb>(
  supplierData?: T,
  photos?: IPhoto[],
): string => {
  const firstPhoto = getSupplierPhotoObject(supplierData?.photos || photos);
  const path = firstPhoto
    ? 'public_id' in firstPhoto
      ? firstPhoto.public_id
      : firstPhoto.path
    : undefined;

  if (path && !supplierData?.customSupplier) {
    const url = `${IMAGES_BASE_URL}/${path}${
      !hasExtension(path) ? `.${(firstPhoto as ISupplierPhoto)?.ext || 'jpg'}` : ''
    }`;
    return `${url}`;
  }

  return `${getSupplierThumbUrl(supplierData)}`;
};
